import { computed } from 'vue'
import { createGlobalState, useAsyncState } from '@vueuse/core'
import { http } from '@/utils/http'
import type { Response } from '@/types/http'

export interface BalanceRes {
  balance?: string | number
  freeze?: string | number
  audit?: string | number
}

async function fetchBalanceApi() {
  return http<Response<BalanceRes>>('/app-api/account/memberAccount/center/balance', {
    method: 'POST',
    body: {}
  })
}

async function oneClickRecycle() {
  return http<Response<BalanceRes>>('/app-api/game/transfer/collectAll', {
    method: 'POST'
  })
}

/**
 * 全局共享-用户中心钱包-余额数据
 */
export const useBalance = createGlobalState(() => {
  // ==== helper =====
  const isValidNumber = (value: unknown) => {
    const text = String(value ?? '').trim()
    return text !== '' && !Number.isNaN(Number(text))
  }
  const unWrapRes = (res: Response<BalanceRes>): BalanceRes | null => {
    return res?.data ?? null
  }
  const updateBalanceState = (res: Response<BalanceRes>): void => {
    const data = unWrapRes(res)
    if (!data) return

    if (isValidNumber(data?.balance)) {
      balanceState.value = {
        ...balanceState.value,
        balance: Number(data.balance)
      }
    }

    if ('audit' in data && isValidNumber(data.audit)) {
      balanceState.value = {
        ...balanceState.value,
        audit: Number(data.audit)
      }
    }
  }

  // === 功能 ===

  // 获取余额 API 封装
  const fetchBalance = async () => {
    const res = await fetchBalanceApi()
    return unWrapRes(res)
  }

  // 获取余额
  const {
    state: balanceState,
    isLoading,
    error,
    execute: refreshBalance
  } = useAsyncState<BalanceRes | null>(fetchBalance, null, {
    immediate: true,
    resetOnExecute: false
  })

  // 一键回收余额 TODO 测试
  const { isLoading: isRecycling, execute: recycleBalance } = useAsyncState(
    async () => {
      const recycleRes = await oneClickRecycle()
      // 先用recycle数据更新一次
      updateBalanceState(recycleRes)
      // 按照H5原逻辑是要继续从balance接口中获取余额的 虽然recycle接口也有数据 (不能用fetchBalance 会触发isLoading
      const res = await fetchBalanceApi()
      // console.log('oneClickRecycle', res, isRecycling)
      updateBalanceState(res)
    },
    null,
    { immediate: false }
  )

  // === 状态 ===
  const balance = computed(() => {
    // console.log('balanceState.value?.balance', balanceState.value?.balance)
    return isValidNumber(balanceState.value?.balance) ? Number(balanceState.value?.balance) : 0
  })
  const audit = computed(() => {
    return isValidNumber(balanceState.value?.audit) ? Number(balanceState.value?.audit) : undefined
  })

  return {
    // 状态：正在回收余额
    isRecycling,
    // 操作：一键回收余额
    recycleBalance,
    // 状态：余额信息
    balance,
    // 取款所需流水
    audit,
    // 状态：获取余额中
    isLoading,
    // 操作：刷新余额
    refreshBalance
  }
})

export type UseBalanceReturn = ReturnType<typeof useBalance>
